import { SET_DEFINITIONS }   from '../../data/sets';
import { formatEnhancement } from '../../data/enhancements';

/**
 * Shared hover tooltip for inventory and equipment slots.
 *
 * Props:
 *   item             — item object
 *   color            — rarity hex colour
 *   lines            — string[] from formatStatMods()
 *   ability          — LEGENDARY_ABILITIES entry | null
 *   equippedSetCount — number | undefined
 *   levelLock        — boolean  (item requires higher player level)
 *   levelReq         — number
 *   userLock         — boolean  (player-set protection lock)
 *   sellPrice        — number | null
 *   lockMode         — boolean
 *   isEquipmentSlot  — boolean  (paper-doll slot — shows "Click to unequip")
 *   tooltipSide      — 'center' (default) | 'right'  — 'right' anchors the
 *                      tooltip to the slot's left edge so it opens rightward,
 *                      preventing overflow for items near the left screen edge
 */
export function ItemTooltip({
  item,
  color,
  lines,
  ability,
  equippedSetCount,
  levelLock   = false,
  levelReq    = 1,
  userLock    = false,
  sellPrice   = null,
  lockMode    = false,
  isEquipmentSlot = false,
  tooltipSide = 'center',
}) {
  const setDef = item.setId ? SET_DEFINITIONS[item.setId] : null;

  return (
    <div className={`item-slot-tooltip${tooltipSide === 'right' ? ' item-slot-tooltip--right' : ''}`}>
      {/* Name + rarity */}
      <div className="tooltip-name"   style={{ color }}>{item.name}</div>
      <div className="tooltip-rarity" style={{ color }}>{item.rarity}</div>

      {/* Level requirement */}
      {isEquipmentSlot ? (
        levelReq > 1 && (
          <div className="tooltip-levelreq" style={{ color: '#aac8f0' }}>
            Level {levelReq}
          </div>
        )
      ) : (
        <div className="tooltip-levelreq" style={{ color: levelLock ? '#ff6655' : '#aac8f0' }}>
          {levelLock ? `⚠️ Requires Level ${levelReq}` : `Level ${levelReq}`}
        </div>
      )}

      {/* User-lock notice (inventory only) */}
      {!isEquipmentSlot && userLock && (
        <div className="tooltip-hint tooltip-hint--locked">
          🔐 Protected — click in lock mode to unlock
        </div>
      )}

      {/* Stat lines */}
      {lines.map(l => (
        <div key={l} className="tooltip-stat">{l}</div>
      ))}

      {/* Legendary ability */}
      {ability && (
        <div className="tooltip-ability">
          <div className="tooltip-ability-name">{ability.name}</div>
          <div className="tooltip-ability-desc">{ability.desc}</div>
        </div>
      )}

      {/* Enhancement block */}
      {item.enhancement && (
        <div className="tooltip-enhancement">
          <div className="tooltip-enh-header">✦ Enhancement  Lv {item.enhancement.level}</div>
          {formatEnhancement(item.enhancement).map(l => (
            <div key={l} className="tooltip-enh-stat">{l}</div>
          ))}
        </div>
      )}

      {/* Set info */}
      {setDef && (
        <div className="tooltip-set-info">
          <div className="tooltip-set-name" style={{ color: '#00e676' }}>
            ◆ {setDef.name} Set
          </div>
          {equippedSetCount !== undefined && (
            <div className="tooltip-set-count" style={{ color: '#aac8f0' }}>
              {equippedSetCount}/{setDef.bonuses[setDef.bonuses.length - 1].count} pieces equipped
            </div>
          )}
          {setDef.bonuses.map(bonus => (
            <div
              key={bonus.count}
              className="tooltip-set-bonus"
              style={{ color: equippedSetCount >= bonus.count ? '#00e676' : '#5a6a7a' }}
            >
              {equippedSetCount >= bonus.count ? '✦' : '○'} {bonus.count} pcs: {bonus.desc}
            </div>
          ))}
        </div>
      )}

      {/* Footer action hint */}
      {isEquipmentSlot ? (
        <div className="tooltip-hint">Click to unequip</div>
      ) : lockMode ? (
        <div className="tooltip-hint">
          {userLock ? '🔓 Click to unlock' : '🔐 Click to lock'}
        </div>
      ) : sellPrice != null ? (
        <div className="tooltip-hint tooltip-hint--sell">
          🪙 Sell for <strong>{sellPrice}g</strong>
        </div>
      ) : levelLock ? (
        <div className="tooltip-hint tooltip-hint--locked">Cannot equip yet</div>
      ) : (
        <div className="tooltip-hint">
          {item.slotType === 'rune' ? 'Click to socket' : 'Click to equip'}
        </div>
      )}
    </div>
  );
}
